import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, connectLive, getToken, setToken } from './api.js';
import { setMarkets, usd } from './format.js';
import { Modal, setBrand, brand } from './ui.jsx';
import { TERMS_VERSION, keyPoints } from './legal.jsx';
import { detectLang, loadLang, t, tErr, useLang } from './i18n.js';
import Terminal from './Terminal.jsx';
import Admin from './Admin.jsx';
import { Affiliates, ApiKeys, BuyChallenge, Leaderboard, Login, TermsPage } from './pages.jsx';

// Pages are addressed by hash so they can be shared: #/leaderboard, #/affiliates, #/admin, #/api
const readRoute = () => (location.hash.replace(/^#\/?/, '').split(/[?#]/)[0] || 'trade');

export default function App() {
  const [cfg, setCfg] = useState(null);
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [prices, setPrices] = useState({});
  const [book, setBook] = useState(null);
  const [symbol, setSymbol] = useState(() => { try { return localStorage.getItem('symbol') || 'BTCUSDT'; } catch { return 'BTCUSDT'; } });
  const [live, setLive] = useState(false);
  const [feed, setFeed] = useState({ source: null, connected: false });
  const [toasts, setToasts] = useState([]);
  const [buying, setBuying] = useState(false);
  const [route, setRoute] = useState(readRoute);
  const [ready, setReady] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const liveRef = useRef(null);
  const lang = useLang();

  const go = useCallback((r) => { location.hash = r === 'trade' ? '#/' : `#/${r}`; setRoute(r); if (r === 'trade') setBuying(false); window.scrollTo(0, 0); }, []);
  useEffect(() => { const f = () => setRoute(readRoute()); window.addEventListener('hashchange', f); return () => window.removeEventListener('hashchange', f); }, []);
  useEffect(() => { try { localStorage.setItem('symbol', symbol); } catch { /* ignore */ } }, [symbol]);

  const toast = useCallback((text, kind = 'info') => {
    const id = Math.random();
    setToasts((t) => [...t.slice(-3), { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  const loadMe = useCallback(async () => {
    if (!getToken()) { setUser(null); setAccounts(null); return; }
    try {
      const me = await api('GET', '/api/me');
      setUser(me.user); setAccounts(me.accounts);
      api('GET', '/api/favorites').then(setFavorites).catch(() => {});
    } catch (e) {
      if (e.status === 401) { setToken(null); setUser(null); setAccounts(null); }
    }
  }, []);

  useEffect(() => {
    captureReferral();
    Promise.all([
      loadLang(detectLang()),
      api('GET', '/api/config').then((c) => { setCfg(c); setBrand(c); }).catch(() => setCfg({})),
      api('GET', '/api/markets').then(setMarkets).catch(() => {}),
      loadMe(),
    ]).finally(() => setReady(true));
  }, [loadMe]);

  useEffect(() => {
    liveRef.current = connectLive({
      status: setLive,
      market: (m) => { setPrices(m.prices); setFeed({ source: m.feed, connected: m.connected }); },
      // Small update with only the prices that moved; untouched markets keep the same object (no re-render).
      px: (m) => {
        const keys = Object.keys(m.p || {});
        if (keys.length) setPrices((prev) => { const next = { ...prev }; for (const s of keys) next[s] = { ...(prev[s] || {}), price: m.p[s][0], ts: m.p[s][1] }; return next; });
        setFeed((f) => (f.connected === m.connected ? f : { ...f, connected: m.connected }));
      },
      book: (m) => setBook(m),
      accounts: (m) => setAccounts(m.accounts),
      event: (m) => {
        const msg = {
          breached: [t('Account breached: a loss limit was reached and positions were closed.'), 'error'],
          advanced: [t('Target reached. Your account moved to the next stage.'), 'success'],
          take_profit: [t('Take-profit hit: position closed in profit.'), 'success'],
          stop_loss: [t('Stop-loss hit: position closed.'), 'info'],
        }[m.event];
        if (msg) toast(...msg);
      },
    });
    return () => liveRef.current.close();
  }, [toast]);

  useEffect(() => { if (liveRef.current) liveRef.current.setBookSymbol(symbol); setBook(null); }, [symbol]);

  useEffect(() => {
    if (!accounts || !accounts.length) return;
    if (!accountId || !accounts.find((a) => a.id === accountId)) {
      const active = accounts.find((a) => a.status === 'active');
      setAccountId((active || accounts[0]).id);
    }
  }, [accounts, accountId]);

  const onSignedIn = async () => { await loadMe(); if (liveRef.current) liveRef.current.reauth(); };
  const signOut = () => { setToken(null); setUser(null); setAccounts(null); setAccountId(null); setFavorites([]); go('trade'); };
  const toggleFavorite = useCallback(async (sym) => {
    const on = favorites.includes(sym);
    setFavorites((f) => (on ? f.filter((x) => x !== sym) : [...f, sym]));
    try { await api(on ? 'DELETE' : 'PUT', `/api/favorites/${sym}`); }
    catch (e) { setFavorites((f) => (on ? [...f, sym] : f.filter((x) => x !== sym))); toast(tErr(e.message), 'error'); }
  }, [favorites, toast]);
  const acceptTerms = async () => {
    await api('POST', '/api/terms/accept', { version: (cfg && cfg.termsVersion) || TERMS_VERSION });
    setUser((u) => ({ ...u, termsAccepted: true }));
  };
  const account = accounts && accounts.find((a) => a.id === accountId);
  const shell = { user, signOut, toast, go };

  let screen;
  if (!ready) screen = <div className="center-screen"><div className="spinner" /></div>;
  else if (route === 'leaderboard') screen = <Leaderboard {...shell} />;
  else if (route === 'terms') screen = <TermsPage {...shell} />;
  else if (!user) screen = <Login cfg={cfg} onSignedIn={onSignedIn} toast={toast} go={go} />;
  else if (route === 'affiliates') screen = <Affiliates {...shell} />;
  else if (route === 'api') screen = <ApiKeys {...shell} />;
  else if (route === 'admin' && user.isAdmin) screen = <Admin {...shell} />;
  else if (!accounts) screen = <div className="center-screen"><div className="spinner" /></div>;
  else if (!accounts.length || buying) {
    screen = (
      <BuyChallenge
        {...shell} cfg={cfg}
        canCancel={accounts.length > 0}
        onCancel={() => setBuying(false)}
        onBought={async (a) => { await loadMe(); setAccountId(a.id); setBuying(false); toast(t('Challenge started. Your {amount} account is ready.', { amount: usd(a.initialBalance, 0) }), 'success'); }}
      />
    );
  } else if (account) {
    screen = (
      <Terminal
        {...shell} accounts={accounts} account={account} setAccountId={setAccountId}
        prices={prices} book={book} symbol={symbol} setSymbol={setSymbol}
        live={live} feed={feed} onNewChallenge={() => setBuying(true)}
        favorites={favorites} toggleFavorite={toggleFavorite}
      />
    );
  } else screen = <div className="center-screen"><div className="spinner" /></div>;

  const needsTerms = ready && user && !user.termsAccepted && route !== 'terms';
  return (
    <React.Fragment key={lang}>
      {user && user.kycStatus && ['required', 'pending', 'rejected'].includes(user.kycStatus) && (
        <div className="kyc-banner" role="status">
          {user.kycStatus === 'rejected'
            ? t('Identity verification was not successful. Rewards are unavailable for this account. Contact support for details.')
            : t('Identity verification is required for your account. Our team will contact you with the next steps. Rewards are paused until verification is complete.')}
          <a href="#/terms#s6">{t('Why?')}</a>
        </div>
      )}
      {screen}
      {needsTerms && <TermsGate onAccept={acceptTerms} onDecline={signOut} toast={toast} />}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((x) => <div key={x.id} className={`toast ${x.kind}`}>{x.text}</div>)}
      </div>
    </React.Fragment>
  );
}

function captureReferral() {
  try {
    const url = new URL(location.href);
    const code = (url.searchParams.get('ref') || '').trim().toUpperCase();
    if (!code) return;
    localStorage.setItem('ref', code);
    if (sessionStorage.getItem('refClick') !== code) {
      sessionStorage.setItem('refClick', code);
      api('POST', '/api/ref/click', { code }).catch(() => {});
    }
    url.searchParams.delete('ref');
    history.replaceState(null, '', url.pathname + url.search + url.hash);
  } catch { /* storage unavailable */ }
}

// Shown after sign-in until the current terms are accepted. Purchases are blocked server-side too.
function TermsGate({ onAccept, onDecline, toast }) {
  const [a, setA] = useState(false);
  const [b, setB] = useState(false);
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    try { await onAccept(); toast(t('Welcome aboard. Good luck with your evaluation!'), 'success'); }
    catch (e) { toast(tErr(e.message), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t('Before you start on {brand}', { brand: brand.name })} onClose={() => {}} wide>
      <div className="modal-body">
        <p className="lede-sm">{t('{brand} is where traders prove their skill. Pass the evaluation, reach Stage 3, and get the chance to be selected for {operator}’s copy-trading program. Here’s how it works:', { brand: brand.name, operator: brand.operator })}</p>
        <ul className="fine big">{keyPoints().map((p, i) => <li key={i}>{p}</li>)}</ul>
        <label className="check"><input type="checkbox" checked={a} onChange={(e) => setA(e.target.checked)} />
          <span>{t('I understand that all trading is simulated with virtual funds, and that I have no claim to any virtual balance or profit.')}</span></label>
        <label className="check"><input type="checkbox" checked={b} onChange={(e) => setB(e.target.checked)} />
          <span>{t('I am 18 or older and I agree to the Terms & Risk Disclosure, including identity verification when requested.')} <a href="#/terms" target="_blank" rel="noreferrer">{t('Read the terms')}</a></span></label>
        <button className="btn primary lg block" disabled={!a || !b || busy} onClick={accept}>{busy ? t('Saving…') : t('I agree, continue')}</button>
        <button className="btn ghost block" onClick={onDecline}>{t('Not now, sign out')}</button>
      </div>
    </Modal>
  );
}
